import { Box, InputBase, Slider, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export type DualRangeSegment = {
  /** Display label for the segment (e.g. a configured stage name). */
  label: string;
  /** Band color used in the rail and legend swatch. */
  color: string;
};

export type DualRangeSliderProps = {
  /** Lower bound of the track (fixed — the slider does not grow dynamically). */
  min: number;
  /** Upper bound of the track (fixed — set to 200 for Guest Journey stages). */
  max: number;
  /** Increment for both dragging and the editable inputs. */
  step?: number;
  /**
   * Increment used only to display the *start* of the next segment in the
   * legend (the previous boundary + this). Defaults to `step`. For money set to
   * `0.01` so a segment ending at $19.00 reads as the next starting at $19.01,
   * while dragging still moves in whole `step` units.
   */
  boundaryStep?: number;
  /**
   * By default the lower handle floors at `min + step`, so the first segment
   * always has width. Set this to let the lower handle reach `min` — used by the
   * visit slider so the first stage can be a single value (e.g. Explorer "1-1").
   */
  allowLowerAtMin?: boolean;
  /** Lower handle value — the boundary between segment 0 and segment 1. */
  lower: number;
  /** Upper handle value — the boundary between segment 1 and segment 2. */
  upper: number;
  /** Fired with the clamped (lower, upper) pair on drag, key, or input edit. */
  onChange: (lower: number, upper: number) => void;
  /**
   * Exactly three segments (low → high). Their colors paint the three rail
   * bands; their labels caption the legend rows.
   */
  segments: [DualRangeSegment, DualRangeSegment, DualRangeSegment];
  /** Formats a boundary value for the legend (e.g. plain number vs `$`). */
  format: (value: number) => string;
  /** Prefix for handle/input aria labels, e.g. "Visit stage". */
  ariaPrefix: string;
  /** Optional text shown inside each editable bubble before the value (e.g. `$`). */
  prefix?: string;
  /**
   * Values at which to draw reference tick marks on the rail — used to show
   * where the recommended default boundaries sit.
   */
  ticks?: number[];
  /** Renders the slider and value bubbles read-only. */
  disabled?: boolean;
  'data-testid'?: string;
};

const clampLower = (
  value: number,
  floor: number,
  upper: number,
  step: number,
) => Math.min(Math.max(value, floor), upper - step);

const clampUpper = (value: number, max: number, lower: number, step: number) =>
  Math.max(Math.min(value, max - step), lower + step);

/**
 * A two-handle range slider with a tri-color rail, **editable value bubbles**
 * floating above each thumb, optional reference tick marks, and a three-segment
 * legend. Used by the Guest Journey Settings page to set the visit-count and
 * average-check stage boundaries. The track is fixed (no dynamic max) — the
 * lower handle floors at `min + step` (or `min` with `allowLowerAtMin`), the
 * upper caps at `max - step`, and the handles never cross.
 */
const DualRangeSlider = ({
  min,
  max,
  step = 1,
  boundaryStep = step,
  allowLowerAtMin = false,
  lower,
  upper,
  onChange,
  segments,
  format,
  ariaPrefix,
  prefix,
  ticks,
  disabled = false,
  'data-testid': dataTestId = 'DualRangeSlider',
}: DualRangeSliderProps) => {
  const { t } = useTranslation();

  const pct = (value: number) =>
    Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  const lowerPct = pct(lower);
  const upperPct = pct(upper);

  // The lower handle normally floors at `min + step` so the first segment keeps
  // width; `allowLowerAtMin` lets it reach `min` for a single-value first stage.
  const lowerFloor = allowLowerAtMin ? min : min + step;

  const [low, mid, high] = segments;

  const handleSlider = (
    _event: Event,
    value: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(value)) return;
    // Locked range: only the dragged thumb moves, capped at the *other* thumb's
    // current position. The handles never push one another.
    if (activeThumb === 0) {
      onChange(clampLower(value[0], lowerFloor, upper, step), upper);
    } else {
      onChange(lower, clampUpper(value[1], max, lower, step));
    }
  };

  const handleLowerInput = (raw: string) => {
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return;
    onChange(clampLower(value, lowerFloor, upper, step), upper);
  };

  const handleUpperInput = (raw: string) => {
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return;
    onChange(lower, clampUpper(value, max, lower, step));
  };

  return (
    <Stack spacing={0} px={1} data-testid={dataTestId}>
      {/* No horizontal padding here: the bubbles are absolutely positioned, so
          their `left: %` must resolve against the same width the slider thumb
          travels (this Box's content box). Any px padding would widen the
          bubbles' containing block and drift them off the thumbs toward the
          ends. Breathing room lives on the outer Stack instead. */}
      <Box position="relative" sx={{ pt: 3 }}>
        <ValueBubble
          pct={lowerPct}
          value={lower}
          step={step}
          prefix={prefix}
          onInput={handleLowerInput}
          disabled={disabled}
          ariaLabel={t('dualRangeSlider.lowerValue', { prefix: ariaPrefix })}
          data-testid={`${dataTestId}.LowerInput`}
        />
        <ValueBubble
          pct={upperPct}
          value={upper}
          step={step}
          prefix={prefix}
          onInput={handleUpperInput}
          disabled={disabled}
          ariaLabel={t('dualRangeSlider.upperValue', { prefix: ariaPrefix })}
          data-testid={`${dataTestId}.UpperInput`}
        />
        <Slider
          value={[lower, upper]}
          min={min}
          max={max}
          step={step}
          marks={ticks?.map(value => ({
            value,
            label: t('dualRangeSlider.tickLabel', { value: format(value) }),
          }))}
          onChange={handleSlider}
          disableSwap
          disabled={disabled}
          data-testid={`${dataTestId}.Slider`}
          aria-label={ariaPrefix}
          getAriaLabel={index =>
            index === 0
              ? t('dualRangeSlider.lowerHandle', { prefix: ariaPrefix })
              : t('dualRangeSlider.upperHandle', { prefix: ariaPrefix })
          }
          sx={{
            // Thicker rail/track (~2x the default 4px).
            height: 8,
            '& .MuiSlider-rail': {
              opacity: 1,
              background: `linear-gradient(to right, ${low.color} 0%, ${low.color} ${lowerPct}%, ${mid.color} ${lowerPct}%, ${mid.color} ${upperPct}%, ${high.color} ${upperPct}%, ${high.color} 100%)`,
            },
            '& .MuiSlider-track': {
              border: 'none',
              backgroundColor: 'transparent',
            },
            // Reference ticks at the recommended default boundaries.
            '& .MuiSlider-mark': {
              height: 18,
              width: 2,
              borderRadius: 0,
              backgroundColor: 'text.primary',
              opacity: 0.55,
            },
            '& .MuiSlider-markActive': {
              backgroundColor: 'text.primary',
              opacity: 0.55,
            },
            // "DEFAULT 10" tick captions below the rail.
            '& .MuiSlider-markLabel': {
              typography: 'caption',
              color: 'text.secondary',
            },
          }}
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        data-testid={`${dataTestId}.Legend`}
      >
        <LegendItem
          color={low.color}
          label={low.label}
          range={`${format(min)} – ${format(lower)}`}
        />
        <LegendItem
          color={mid.color}
          label={mid.label}
          range={`${format(lower + boundaryStep)} – ${format(upper)}`}
        />
        <LegendItem
          color={high.color}
          label={high.label}
          range={t('dualRangeSlider.openEnded', {
            min: format(upper + boundaryStep),
          })}
        />
      </Stack>
    </Stack>
  );
};

type ValueBubbleProps = {
  /** Horizontal position as a percentage of the track (aligns with the thumb). */
  pct: number;
  value: number;
  step: number;
  prefix?: string;
  onInput: (raw: string) => void;
  ariaLabel: string;
  disabled: boolean;
  'data-testid': string;
};

/**
 * A small editable "popover" pinned above a slider thumb. It is a sibling of the
 * MUI `Slider` (not a child), so editing it never starts a thumb drag, and the
 * thumb below stays the accessible drag/keyboard target.
 *
 * While focused, the field holds a free-text **draft** so partial entries (e.g.
 * typing "4" on the way to "40") aren't clamped mid-keystroke. The clamp +
 * `onInput` commit fires only on **Enter or blur**; an empty / non-numeric draft
 * is discarded and the field reverts to the live value.
 */
const ValueBubble = ({
  pct,
  value,
  step,
  prefix,
  onInput,
  ariaLabel,
  disabled,
  'data-testid': testid,
}: ValueBubbleProps) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    onInput(draft);
    setDraft(null);
  };

  return (
    <Box
      position="absolute"
      data-testid={`${testid}.Bubble`}
      sx={{
        top: 0,
        left: `${pct}%`,
        transform: 'translateX(-50%)',
        // Slight dim signals read-only while keeping the value legible.
        opacity: disabled ? 0.8 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        // Inverted against the theme: black-on-white in light mode, white-on-black
        // in dark mode. `text.primary` / `background.paper` flip together per mode.
        backgroundColor: 'text.primary',
        color: 'background.paper',
        boxShadow: 1,
        // Downward caret so the bubble reads as a popover over the thumb; matches
        // the bubble background so it looks like one shape.
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: '4px solid',
          borderTopColor: 'text.primary',
        },
      }}
    >
      {prefix && (
        <Typography variant="caption" color="inherit">
          {prefix}
        </Typography>
      )}
      <InputBase
        type="number"
        value={draft ?? value}
        onChange={event => setDraft(event.target.value)}
        onBlur={commit}
        disabled={disabled}
        inputProps={{
          step,
          'aria-label': ariaLabel,
          'data-testid': testid,
          onKeyDown: event => {
            if (event.key === 'Enter') commit();
          },
          style: { width: 36, textAlign: 'center', padding: 0 },
        }}
        sx={{
          fontSize: '0.8rem',
          // Inherit the inverted bubble text color for the input glyphs + caret.
          color: 'inherit',
          backgroundColor: 'transparent',
          '& input': { color: 'inherit' },
          // MUI greys disabled inputs (root `color` + input
          // `-webkit-text-fill-color`, both via the more specific `.Mui-disabled`),
          // which vanishes against the inverted bubble. Pin the bubble text color
          // so the value stays legible when read-only.
          '&.Mui-disabled': { color: 'background.paper' },
          '& input.Mui-disabled': {
            color: 'background.paper',
            WebkitTextFillColor: 'currentColor',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            { WebkitAppearance: 'none', margin: 0 },
          '& input[type=number]': { MozAppearance: 'textfield' },
        }}
      />
    </Box>
  );
};

type LegendItemProps = {
  color: string;
  label: string;
  range: string;
};

const LegendItem = ({ color, label, range }: LegendItemProps) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: color,
      }}
    />
    <Typography variant="body2">{label}</Typography>
    <Typography variant="body2" fontWeight={700}>
      {range}
    </Typography>
  </Stack>
);

export default DualRangeSlider;
